import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner, Countdown } from "@/components/Kit";
import { playSfx } from "@/lib/sound";
import * as Icons from "lucide-react";
import { Trash2, Recycle, Package, Clock } from "lucide-react";

function toPascal(s){return (s||"").split("-").map(w=>w.charAt(0).toUpperCase()+w.slice(1)).join("");}

export default function ScrapYard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const load = () => api.get("/scrapyard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <PageLoader label="Sifting the Scrap Yard" />;

  const forage = async () => {
    setBusy(true);
    try { const { data: res } = await api.post("/scrapyard/forage"); playSfx("pop"); toast.success(res.message); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const claim = async (sc) => {
    setClaiming(sc.scrap_id);
    try { const { data: res } = await api.post("/scrapyard/claim", { pet_id: sc.scrap_id }); playSfx("coin"); toast.success(`Salvaged ${res.item?.name || "part"}!`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setClaiming(null); }
  };

  return (
    <div data-testid="scrapyard-page">
      <span className="label-cap text-primary flex items-center gap-2"><Recycle className="w-4 h-4" /> Scrap Yard // Rusty</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-6 font-head">Scrap Yard</h1>

      <div className="panel-glow p-5 mb-6 flex items-center gap-4 hud-corner">
        <Trash2 className="w-10 h-10 text-secondary" />
        <div className="flex-1">
          <h3 className="font-head font-bold">Daily Trash Foraging</h3>
          <p className="text-sm text-muted-foreground">{data.can_forage ? "Dig through the junk for salvage — once per day." : <>Already foraged. Next dig in <Countdown seconds={data.next_forage_in} onDone={load} testId="forage-timer" className="text-secondary" />.</>}</p>
        </div>
        <button onClick={forage} data-testid="forage-btn" disabled={!data.can_forage || busy} className="btn-primary">{busy ? <Spinner className="w-4 h-4" /> : "Forage"}</button>
      </div>

      <h2 className="font-head font-bold text-lg mb-3">Discarded by other pilots <span className="label-cap">(24h decay)</span></h2>
      {data.discards.length === 0 ? (
        <div className="panel p-8 text-center text-muted-foreground">The pile is empty right now. Check back later.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.discards.map((sc) => {
            const Icon = Icons[toPascal(sc.icon)] || Package;
            return (
              <div key={sc.scrap_id} data-testid={`discard-${sc.scrap_id}`} className="panel p-4 flex flex-col items-center text-center">
                <span className="w-10 h-10 rounded-[var(--radius)] bg-muted grid place-items-center mb-2"><Icon className="w-5 h-5 text-muted-foreground" /></span>
                <div className="font-head font-bold text-sm">{sc.name}</div>
                <div className="label-cap text-[9px] mb-1">from {sc.from_name || "unknown"}</div>
                <div className="label-cap text-[9px] mb-2 flex items-center gap-1 text-accent"><Clock className="w-3 h-3" />decays in <Countdown seconds={sc.expires_in} onDone={load} testId={`decay-${sc.scrap_id}`} /></div>
                <button onClick={() => claim(sc)} data-testid={`claim-scrap-${sc.scrap_id}`} disabled={claiming === sc.scrap_id} className="btn-ghost text-xs px-3 py-1.5 w-full">{claiming === sc.scrap_id ? <Spinner className="w-3 h-3" /> : "Salvage"}</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
